import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ClientEnvironment, ConfigObjectModel, ENVIRONMENT, ViewType } from '@thehubz/client/core';
import { Collection, CollectionsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { Subscription, debounce, tap, timer } from 'rxjs';
import { CollectionInfo } from '../collection-info.model';
import { CollectionsListComponent } from '../collections-list/collections-list.component';
import { CollectionAssetPreviewService } from '../services/collection-asset-preview.service';
import { CollectionEventsService } from '../services/collection-events.service';

@Component({
  selector: 'thehubz-collection-publishing',
  templateUrl: './collection-publishing.component.html',
  styleUrls: ['./collection-publishing.component.scss'],
})
export class CollectionPublishingComponent implements OnInit, OnDestroy {
  collectionList: CollectionInfo[] = [];
  collectionSplitList?: CollectionInfo[][];
  basketDataLoaded = false;
  configObject: ConfigObjectModel = {
    title: $localize`Published collection`,
    callFrom: 'publishCollection',
  };

  defaultSort: { [key: string]: number } = { createdAt: -1 };
  sortData: { [key: string]: number } = {};

  filterObject = {
    filters: {
      assetId: '',
      search: '',
    },
    sort: this.defaultSort,
  };
  collectionListSearchFilter!: Subscription;
  size = 4;
  selectedViewSection2: ViewType = 'sortByLastCreated';
  sortByNew = true;
  initialFlag = false;
  readonly brandingKey: CustomClients;

  /**
   * True when data is loading from server
   */
  httpCallInProgress = false;

  @ViewChild(CollectionsListComponent) collections!: CollectionsListComponent;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private collectionAPI: CollectionsApiService,
    private collectionAssetPreviewService: CollectionAssetPreviewService,
    private collectionEventsService: CollectionEventsService,
  ) {
    this.brandingKey = this.env.brandingKey;
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.size = 8;
    }
  }

  ngOnInit(): void {
    // resigning empty string because search value assigned at parent component (collection.component.ts)
    this.collectionEventsService.collectionListSearchFilter$.next('');
    this._showLoader();
    // subscribe to the search filter
    this.collectionListSearchFilter = this.collectionEventsService.collectionListSearchFilter$.pipe(debounce(() => timer(500))).subscribe((data) => {
      if (data) {
        this.initialFlag = false;
        this.fetchCollections(data);
      } else {
        if (!this.initialFlag) {
          this.initialFlag = true;
          this.fetchCollections();
        }
      }
    });
  }

  /**
   * get all collection list of logged in user
   * @param searchText
   */
  async fetchCollections(searchText?: string, showBubbles?: boolean) {
    this.filterObject['filters']['search'] = searchText || '';
    this.collectionAPI
      .collectionsControllerFindAllPublishedCollection(JSON.stringify(this.filterObject))
      .pipe(tap(() => this._showLoader(false)))
      .subscribe({
        next: async (response) => {
          this.collectionList = response.result;
          if (this.collectionList.length) {
            this.collectionList = await this.collectionAssetPreviewService.generateCoverPreview(this.collectionList);
            this.collectionSplitList = this.split(this.collectionList, this.size);
            if (showBubbles) {
              this.collections.showSearchFilterBubbles(searchText);
            }
            this.basketDataLoaded = true;
          } else {
            this.basketDataLoaded = true;
          }
        },
      });
  }

  sortBy(event: AnyType) {
    this.sortByNew = !this.sortByNew;
    this.selectedViewSection2 = event || 'sortByLastCreated';
    if (!this.sortByNew) {
      this.sortData = { name: 1 };
    } else {
      this.sortData = this.defaultSort;
    }
    this.filterObject['sort'] = this.sortData;
    this.fetchCollections();
  }

  private split(input: Collection[], groupSize: number) {
    const out = [];
    for (let i = 0; i < input.length; i += groupSize) {
      out.push(input.slice(i, i + groupSize));
    }
    return out;
  }

  publicCollectionChanged(action: string, data: any) {
    switch (action) {
      case 'updated': {
        const collectionIndex = this.collectionList.findIndex((collection: Collection) => collection._id === data._id);
        this.collectionList[collectionIndex] = data;
        break;
      }
      case 'showBubbles': {
        this.fetchCollections(data, true);
        break;
      }
      case 'unpublished':
      default: {
        this.fetchCollections();
        break;
      }
    }
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }

  ngOnDestroy(): void {
    this.collectionListSearchFilter?.unsubscribe();
  }
}
